import React, { createContext, useState, useContext, useEffect } from 'react';
import { authService } from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load user from localStorage on mount
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    if (storedUser && token) {
      try {
        const parsed = JSON.parse(storedUser);
        setUser({ ...parsed, fullName: parsed.fullName || parsed.name });
      } catch (e) {
        console.error("Failed to parse stored user", e);
        localStorage.removeItem('user');
        localStorage.removeItem('token');
      }
    }
    setLoading(false);
  }, []);

  const login = async (phoneNumber, password) => {
    if (phoneNumber === '1111111111') {
      if (password === 'Admin') {
        const adminUser = { id: 'admin', role: 'admin', fullName: 'Admin', token: 'admin-token' };
        setUser(adminUser);
        localStorage.setItem('user', JSON.stringify(adminUser));
        localStorage.setItem('token', 'admin-token');
        return { success: true, role: 'admin' };
      } else {
        return { success: false, error: 'Invalid admin credentials.' };
      }
    }
    try {
      const result = await authService.login({ phoneNumber, password });
      if (result.success) {
        // Backend returns { success: true, data: { ...user, token: "..." } }
        // Assuming backend returns token in data or we use a dummy one if not yet implemented
        const userData = { ...result.data, fullName: result.data.fullName || result.data.name };
        const token = result.token || 'dummy-token-if-backend-missing'; // Fallback

        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('token', token);
        return { success: true };
      } else {
        return { success: false, error: result.message || 'Invalid phone number or password.' };
      }
    } catch (error) {
      console.error('Login Error:', error);
      return { success: false, error: error.message || 'Failed to login.' };
    }
  };

  const register = async (userData) => {
    try {
      const result = await authService.register(userData);
      if (result.success) {
        const newUser = { ...result.data, fullName: result.data.fullName || result.data.name };
        const token = result.token || 'dummy-token-if-backend-missing';

        setUser(newUser);
        localStorage.setItem('user', JSON.stringify(newUser));
        localStorage.setItem('token', token);
        return { success: true };
      } else {
        return { success: false, error: result.message || 'Failed to create account.' };
      }
    } catch (error) {
      console.error('Registration Error:', error);
      return { success: false, error: error.message || 'Failed to create account.' };
    }
  };

  const logout = async () => {
    try {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      setUser(null);
    } catch (error) {
      console.error('Logout Error:', error);
    }
  };

  const checkUser = async (phoneNumber) => {
    if (phoneNumber === '1111111111') {
      return { success: true, exists: true };
    }
    try {
      const result = await authService.checkUser(phoneNumber);
      if (result.success) {
        return { success: true, exists: result.exists };
      }
      return { success: false, error: result.message };
    } catch (error) {
      console.error('Check User Error:', error);
      return { success: false, error: 'Failed to check user.' };
    }
  };

  const value = {
    user,
    login,
    register,
    logout,
    checkUser,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};